import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Navbar } from '@/components/Navbar';
import { Button } from '@/components/ui/button';
import { ArrowUpRight, ArrowDownRight, ArrowLeft } from 'lucide-react';
import { toast } from 'sonner';
import { BrandWatermark } from '@/components/BrandWatermark';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function SignalDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [signal, setSignal] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchSignal();
  }, [id]);

  const fetchSignal = async () => {
    try {
      const response = await axios.get(`${API}/signals/${id}`);
      setSignal(response.data);
    } catch (error) {
      toast.error('Error al cargar señal');
      navigate('/dashboard');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#020402]">
        <Navbar />
        <div className="pt-24 flex items-center justify-center h-96">
          <div className="inline-block h-8 w-8 animate-spin rounded-full border-4 border-solid border-emerald-500 border-r-transparent"></div>
        </div>
      </div>
    );
  }

  if (!signal) return null;

  return (
    <div className="min-h-screen bg-[#020402]">
      <Navbar />
      <BrandWatermark />
      
      <div className="pt-24 pb-12 px-6">
        <div className="container mx-auto max-w-4xl">
          {/* Back Button */}
          <Button 
            onClick={() => navigate('/dashboard')}
            variant="ghost"
            className="mb-6 text-emerald-400 hover:text-emerald-300"
            data-testid="back-btn"
          >
            <ArrowLeft className="h-4 w-4 mr-2" />
            Volver a Señales
          </Button>

          {/* Signal Detail Card */}
          <div className="glass-panel rounded-lg p-8" data-testid="signal-detail">
            {/* Header */}
            <div className="flex justify-between items-start mb-6">
              <div>
                <span className={signal.signal_type === 'buy' ? 'signal-badge-buy' : 'signal-badge-sell'}>
                  {signal.signal_type === 'buy' ? (
                    <><ArrowUpRight className="inline h-4 w-4" /> COMPRA</>
                  ) : (
                    <><ArrowDownRight className="inline h-4 w-4" /> VENTA</>
                  )}
                </span>
              </div>
              <span className={`text-sm px-3 py-1 rounded ${
                signal.status === 'active' 
                  ? 'bg-emerald-500/20 text-emerald-400 border border-emerald-500/30' 
                  : 'bg-gray-500/20 text-gray-400 border border-gray-500/30'
              }`}>
                {signal.status === 'active' ? 'ACTIVA' : 'CERRADA'}
              </span>
            </div>

            {/* Title & Symbol */}
            <h1 
              className="text-4xl font-bold mb-2 text-emerald-50"
              style={{ fontFamily: 'Rajdhani, sans-serif' }}
            >
              {signal.title}
            </h1>
            <p className="text-3xl font-mono font-bold text-emerald-400 mb-8">{signal.symbol}</p>

            {/* Chart Image */}
            {signal.chart_image_url && (
              <div className="mb-8 border border-emerald-500/30 rounded-lg overflow-hidden alien-glow">
                <img 
                  src={signal.chart_image_url} 
                  alt="Trading Chart"
                  className="w-full"
                  data-testid="chart-image"
                />
              </div>
            )}

            {/* Price Information */}
            <div className="grid md:grid-cols-3 gap-6 mb-8">
              <div className="bg-black/30 border border-emerald-500/20 rounded-lg p-6">
                <p className="text-emerald-100/60 text-sm mb-2">PRECIO DE ENTRADA</p>
                <p className="text-3xl font-mono font-bold text-emerald-50">${signal.entry_price}</p>
              </div>
              
              {signal.stop_loss && (
                <div className="bg-black/30 border border-red-500/20 rounded-lg p-6">
                  <p className="text-emerald-100/60 text-sm mb-2">STOP LOSS</p>
                  <p className="text-3xl font-mono font-bold text-red-400">${signal.stop_loss}</p>
                </div>
              )}
              
              {signal.take_profits?.length > 0 ? (
                <div className="bg-black/30 border border-emerald-500/20 rounded-lg p-6">
                  <p className="text-emerald-100/60 text-sm mb-2">TAKE PROFITS</p>
                  <div className="space-y-2">
                    {signal.take_profits.map((tp, i) => (
                      <div key={i} className="flex justify-between items-center">
                        <span className="text-emerald-400 text-xs font-bold">TP{i + 1}</span>
                        <span className="text-2xl font-mono font-bold text-emerald-400">${tp}</span>
                      </div>
                    ))}
                  </div>
                </div>
              ) : signal.take_profit ? (
                <div className="bg-black/30 border border-emerald-500/20 rounded-lg p-6">
                  <p className="text-emerald-100/60 text-sm mb-2">TAKE PROFIT</p>
                  <p className="text-3xl font-mono font-bold text-emerald-400">${signal.take_profit}</p>
                </div>
              ) : null}
            </div>

            {/* Risk/Reward Ratio */}
            {signal.risk_reward_ratio && (
              <div className="bg-gradient-to-r from-emerald-500/10 to-transparent border border-emerald-500/30 rounded-lg p-6 mb-8">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-emerald-100/60 text-sm mb-1">RIESGO / RECOMPENSA</p>
                    <p className="text-4xl font-mono font-bold text-emerald-400">1 : {signal.risk_reward_ratio}</p>
                  </div>
                  <div className="text-right">
                    <p className="text-emerald-100/60 text-sm">
                      {signal.risk_reward_ratio >= 2 ? 'Excelente ratio' : signal.risk_reward_ratio >= 1.5 ? 'Buen ratio' : 'Ratio aceptable'}
                    </p>
                  </div>
                </div>
              </div>
            )}

            {/* Results (if closed) */}
            {signal.result && (
              <div className={`border rounded-lg p-6 mb-8 ${
                signal.result === 'won' 
                  ? 'bg-green-500/10 border-green-500/30' 
                  : 'bg-red-500/10 border-red-500/30'
              }`}>
                <h3 className="text-xl font-bold mb-4 text-emerald-50" style={{ fontFamily: 'Rajdhani, sans-serif' }}>
                  RESULTADO DE LA OPERACIÓN
                </h3>
                <div className="grid md:grid-cols-4 gap-4">
                  <div>
                    <p className="text-emerald-100/60 text-sm mb-1">Estado</p>
                    <p className={`text-2xl font-bold ${signal.result === 'won' ? 'text-green-400' : 'text-red-400'}`}>
                      {signal.result === 'won' ? '✓ GANADA' : '✗ PERDIDA'}
                    </p>
                  </div>
                  {signal.close_price && (
                    <div>
                      <p className="text-emerald-100/60 text-sm mb-1">Precio de Cierre</p>
                      <p className="text-2xl font-mono font-bold text-emerald-50">${signal.close_price}</p>
                    </div>
                  )}
                  {signal.pips !== null && (
                    <div>
                      <p className="text-emerald-100/60 text-sm mb-1">Pips</p>
                      <p className={`text-2xl font-mono font-bold ${signal.pips >= 0 ? 'text-green-400' : 'text-red-400'}`}>
                        {signal.pips > 0 ? '+' : ''}{signal.pips}
                      </p>
                    </div>
                  )}
                  {signal.profit_percentage !== null && (
                    <div>
                      <p className="text-emerald-100/60 text-sm mb-1">Ganancia</p>
                      <p className={`text-2xl font-mono font-bold ${signal.profit_percentage >= 0 ? 'text-green-400' : 'text-red-400'}`}>
                        {signal.profit_percentage > 0 ? '+' : ''}{signal.profit_percentage}%
                      </p>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Description */}
            <div>
              <h3 className="text-xl font-bold mb-4 text-emerald-50" style={{ fontFamily: 'Rajdhani, sans-serif' }}>
                ANÁLISIS
              </h3>
              <p className="text-emerald-100/80 leading-relaxed whitespace-pre-wrap">{signal.description}</p>
            </div>

            {/* Metadata */}
            <div className="mt-8 pt-6 border-t border-emerald-500/20 text-sm text-emerald-100/50">
              <p>Publicado: {new Date(signal.created_at).toLocaleString('es-ES')}</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}